import * as React from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import ExpandMore from '@mui/icons-material/ExpandMore'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import { OverflowDetector } from '@sales-center/components'
import { LocationLink } from '@user/components/Location'

import { useData } from './useData'
import { BillTo } from '@user/store/entityLocations'
import { useToggleBoolean } from '@user/utils/useToggleBoolean'
import { useTheme } from '@mui/material/styles'

type Props = {
  headerContent?: React.ReactNode
  location: BillTo
  splitAddressIndex?: number
  isClearable?: boolean
  onClearClick?: () => void
  viewOnly?: boolean
}

export function TidalBillToSelectCardContents(props: Props) {
  const { headerContent, location, isClearable, onClearClick, viewOnly } = props

  const [open, toggleOpen] = useToggleBoolean()
  const { properties } = useData(props)
  const hasMoreFields = properties.length > 2
  const verb = 'Details'
  const theme = useTheme()
  return (
    <Box position={'relative'}>
      <Box mb={viewOnly ? 0 : 1} justifyContent="space-between">
        <Typography variant="subtitle2" fontWeight={500}>
          Bill - to
        </Typography>
        <Typography variant="subtitle2" fontWeight={500}>
          {headerContent}
        </Typography>
      </Box>
      <Box>
        <LocationLink
          icon
          externalLocationCode={location.number}
          externalLocationId={location.id}
          billingLocation
        />
      </Box>
      {viewOnly ? (
        <Box pr={2}>
          {properties.map((property) => (
            <Typography key={property} variant="body2">
              {property}
            </Typography>
          ))}
        </Box>
      ) : (
        <Box pr={2}>
          <OverflowDetector open={open} closedHeight={42}>
            {properties.map((property) => (
              <Typography key={property} variant="body2">
                {property}
              </Typography>
            ))}
          </OverflowDetector>
          {hasMoreFields && (
            <Button
              variant="text"
              size="small"
              sx={{
                textTransform: 'capitalize',
                marginTop: theme.spacing(1),
              }}
              onClick={toggleOpen}
              data-test-id="billToDetailsButton"
            >
              {verb}
              <ExpandMore
                sx={{
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transitionProperty: 'all',
                  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                  transitionDuration: '150ms',
                }}
              />
            </Button>
          )}
        </Box>
      )}
      {isClearable && !viewOnly && (
        <IconButton
          onClick={onClearClick}
          sx={{
            color: theme.palette.primary.main,
            padding: theme.spacing(1),
            position: 'absolute',
            right: '-23px',
            top: '50%',
            transform: 'translate(0, -50%)',
          }}
          data-test-id="billToClearButton"
        >
          <DeleteOutlineIcon />
        </IconButton>
      )}
    </Box>
  )
}
